import { useState, useCallback, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { 
  useChat
} from '@livekit/components-react';
import { Send, Lightbulb, Image, Loader2, AudioLines, Users, MessageSquare, Video, MessageCircle, ChevronUp, ChevronDown, Phone, Mic, MicOff, PhoneOff, Eye, EyeOff, Play } from 'lucide-react';
import Link from 'next/link';
import React from 'react';
import { useTranslations } from 'next-intl';
import { ChatStatus } from '../../data/globalChatState';
import { useChatStatus, useGlobalChatState, useGlobalLiveKitRoomState } from '../../hooks/useGlobalChatState';
import { useAuth } from '@/app/user-and-session/session-auth/hooks/useAuth';
import { useUserAppData } from '@/app/user-and-session/user-app-data/useUserAppData';
import { useDataReceived, useManualImageGeneration } from '../../hooks/useDataReceived';
import { useSendText2API } from '../../hooks/useChatSendApi';
import { useInactivityTimer } from '../../hooks/useGlobalChatState';
import { useHint } from '../../hooks/useHint';
import { HintPanel } from './HintPanel';
import { ImageOptionsPanel } from './ImageOptionsPanel';
import InsufficientCreditPopup from '@/app/popup/InsufficientCreditPopup';
import SubscriptionPopup from '@/app/subscription/billing/frontend/subscriptionPopup';
import { getCurrentModelPrice, getCurrentVoicePrice, getCurrentImageModelPrice } from '../../data/chatSetting/settingOptions';
import { AVAILABLE_VOICES } from '@/app/lib/types/voice';
import { LiveVoiceEditPopup } from '../shared/LiveVoiceEditPopup';
import { useCharacterData } from '../../hooks/useGlobalChatState';
import ImageGenPopup from './ImageGenPopup';
import ImageGenPopupNew from './ImageGenPopupNew';
import PopupTemplate from '@/app/ui/popupTemplate';
import LoginPopup from '@/app/popup/login-popup';
import { useGeminiLive } from '../../hooks/useGeminiLive';
import { useDevice } from '@/app/lib/contexts/DeviceContext';


// Shared wrapper component for consistent chat control dimensions
interface ChatControlWrapperProps {
  children?: React.ReactNode;
  className?: string;
}

export function ChatControlWrapper({ children, className = "" }: ChatControlWrapperProps) {
  return (
    <div 
      className={`flex-shrink-0 min-h-[80px] ${className}`}
      style={{
        background: 'linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, 0.6) 40%, rgba(0, 0, 0, 0.6) 100%)'
      }}
    >
      {children}
    </div>
  );
}

// add new LiveChatControl here
interface LiveChatControlProps {
  onInsufficientCredit?: () => void;
  onRegenRequest?: (triggerRegen: () => void) => void;
  onStartChat?: () => void;
  onInChatImageGen?: (prompt: string) => void; // Callback for in-chat image generation
  onReceivedFirstTokenChange?: (value: boolean) => void; // Callback for receivedFirstToken changes
  showMessages?: boolean; // Whether to show messages
  onToggleShowMessages?: () => void; // Callback to toggle message visibility
}

export function LiveChatControl({ onInsufficientCredit, onRegenRequest, onStartChat, onInChatImageGen, onReceivedFirstTokenChange, showMessages, onToggleShowMessages }: LiveChatControlProps) {
  const t = useTranslations('chat');
  // Global state hooks
  const [globalChatStateData, globalChatStateActions] = useGlobalChatState();
  const { chatStatus, setChatStatus } = useChatStatus();
  
  // Get room from global state instead of context
  const { globalLKRoomState } = useGlobalLiveKitRoomState();
  const room = globalLKRoomState.room;
  
  // Get character data for voice info
  const { character } = useCharacterData();
  
  // Session hook for credit checking
  const { session, userEmail, isAuthenticated, isLoading: authLoading } = useAuth();
  
  // User app data hook for gem count (singleton) - only after auth loading completes
  const [userAppDataState] = useUserAppData(
    !authLoading && isAuthenticated && userEmail ? userEmail : ''
  );
  
  // Local UI state
  const [inputValue, setInputValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInsufficientCreditPopup, setShowInsufficientCreditPopup] = useState(false);
  const [showSubscriptionPopup, setShowSubscriptionPopup] = useState(false);
  const [showVoicePopup, setShowVoicePopup] = useState(false);
  const [showLiveVoicePopup, setShowLiveVoicePopup] = useState(false);
  const [showImageGenPopup, setShowImageGenPopup] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [showMoreImageOption, setShowMoreImageOption] = useState(false);
  const [showMoreImageOptionCall, setShowMoreImageOptionCall] = useState(false);
  // Device detection
  const {isMobile, isDesktop } = useDevice();
  
  // Loading state for LOADING status
  const [loadingTimer, setLoadingTimer] = useState(0);
  const [loadingText, setLoadingText] = useState(t('connecting'));

  // Gemini Live voice chat hook
  const geminiLive = useGeminiLive({
    avatarName: character?.metadata.avatar_name || 'Assistant',
    characterPrompt: character?.metadata.prompt || '',
    userPreferredName: userAppDataState.userPreferredName || 'User'
  });

  // Extract streaming state from geminiLive for UI controls
  const { streamingUserMessageId, streamingAssistantMessageId, onGreetingTurn, muted, setMuted } = geminiLive;

  // Consolidated disabled state
  const isDisabledDueToLoading = chatStatus === ChatStatus.LOADING ||
                                 isSubmitting ||
                                 globalChatStateData.isImageGenerating ||
                                 (streamingUserMessageId !== null && streamingAssistantMessageId !== null) ||
                                 onGreetingTurn;
  
  // Get current chat URL for callback
  const currentChatUrl = globalChatStateData.character?.metadata.avatar_id
    ? `/chat/${globalChatStateData.character.metadata.avatar_id}`
    : '/my-chat';
  
  // Refs
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const lightbulbButtonRef = useRef<HTMLButtonElement>(null);
  const imageButtonRef = useRef<HTMLButtonElement>(null);
  const imageButtonCallRef = useRef<HTMLButtonElement>(null);

  // Use the new modularized data received hook (for LiveKit backward compatibility)
  useDataReceived();

  // Use inactivity timer for typing reset (other resets are handled automatically in data hooks)
  const { resetInactivityTimer } = useInactivityTimer();

  // Use the new API-based send message hook
  const { sendText2API, receivedFirstToken } = useSendText2API({
    avatarId: character?.metadata.avatar_id || '',
    avatarName: character?.metadata.avatar_name || 'Assistant',
    characterPrompt: character?.metadata.prompt || '',
    userPreferredName: userAppDataState.userPreferredName || 'User'
  });

  // Use the new API-based hint hook
  const hintState = useHint({
    avatarId: character?.metadata.avatar_id || '',
    avatarName: character?.metadata.avatar_name || 'Assistant',
    characterPrompt: character?.metadata.prompt || '',
    userPreferredName: userAppDataState.userPreferredName || 'User'
  });

  // Use the manual image generation hook
  const { sendManualImageGen } = useManualImageGeneration();

  // Auto-resize textarea based on content
  useEffect(() => {
    if (inputRef.current) {
      const textarea = inputRef.current;
      
      // Reset height to get proper scrollHeight
      textarea.style.height = 'auto';
      
      // Calculate the new height
      const scrollHeight = textarea.scrollHeight;
      const minHeight = 28; // min-h-[28px] = 1 line
      const maxHeight = window.innerHeight * 0.5; // 50% of screen height
      
      // Set the height with maximum limit of 50% screen height
      const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);
      textarea.style.height = `${newHeight}px`;
      
      // Enable scrolling if content exceeds max height
      if (scrollHeight > maxHeight) {
        textarea.style.overflowY = 'auto';
      } else {
        textarea.style.overflowY = 'hidden';
      }
    }
  }, [inputValue,  // type
      chatStatus, muted ]); // call status and mic status

  // Auto-focus the input field when component mounts
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Notify parent of receivedFirstToken changes
  useEffect(() => {
    if (onReceivedFirstTokenChange) {
      onReceivedFirstTokenChange(receivedFirstToken);
    }
  }, [receivedFirstToken, onReceivedFirstTokenChange]);

  // Reset timer when chat state changes to LOADING
  useEffect(() => {
    if (chatStatus === ChatStatus.LOADING) {
      setLoadingTimer(0);
      setLoadingText(t('connecting'));
    }
  }, [chatStatus, t]);
  
  // Timer effect for progressive text changes during LOADING
  useEffect(() => {
    if (chatStatus === ChatStatus.LOADING) {
      const interval = setInterval(() => {
        setLoadingTimer(prev => {
          const newTime = prev + 1;
          if (newTime === 6) {
            setLoadingText(t('almostThere'));
          } else if (newTime === 30) {
            setLoadingText(t('considerRefreshing'));
          }
          return newTime;
        });
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [chatStatus, t]);

  // Helper function to check authentication and show login popup if needed
  const checkAuthAndProceed = (callback: () => void) => {
    // Check if user is not authenticated (not loading and not authenticated)
    if (!authLoading && !isAuthenticated) {
      setShowLoginPopup(true);
      return false;
    }
    
    // Check if user is authenticated but not onboarded yet (using userAppDataState)
    if (!authLoading && isAuthenticated && !userAppDataState.isInitialized) {
      // User is authenticated but not onboarded - do nothing
      return false;
    }
    
    // If authenticated and onboarded (or still loading), proceed with the action
    callback();
    return true;
  };

  const handleImageGenClick = () => {
    checkAuthAndProceed(() => {
      // Reset hint state when opening image gen popup
      hintState.resetHintState();
      
      // Open the image generation popup
      setShowImageGenPopup(true);
    });
  };

  const handleShowMeClick = async () => {
    // Check authentication first
    if (!checkAuthAndProceed(() => {})) {
      return;
    }

    // Check if user has sufficient credits for image generation
    try {
      const currentImageModelPrice = getCurrentImageModelPrice(globalChatStateData.userChatSetting.imageModel);

      // Use singleton gem count for faster access
      if (userAppDataState.creditCount + userAppDataState.dailyCreditCount < currentImageModelPrice) {
        // Show insufficient credit popup
        setShowInsufficientCreditPopup(true);
        // Call parent callback if provided
        if (onInsufficientCredit) {
          onInsufficientCredit();
        }
        return;
      }
    } catch (error) {
      console.error('Error checking image credits:', error);
      // If credit check fails, still allow proceeding (fail-safe approach)
    }

    // Trigger in-chat image generation directly
    if (onInChatImageGen) {
      onInChatImageGen('');
    }
  };

  const handleShowMeBoobsClick = () => {
    // Auto add "Show me boobs" text into text input
    setInputValue(t('showMeBoobs'));
    // Focus the input after adding text
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleShowMeNakedClick = () => {
    // Auto add "Show me naked" text into text input
    setInputValue(t('showMeNaked'));
    // Focus the input after adding text
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleCustomizeCharacterClick = () => {
    checkAuthAndProceed(() => {
      // Open the image generation popup (same as original image gen button)
      setShowImageGenPopup(true);
    });
  };

  const handleToggleMoreImageOptions = () => {
    setShowMoreImageOption(!showMoreImageOption);
  };

  const handleToggleMoreImageOptionsCall = () => {
    setShowMoreImageOptionCall(!showMoreImageOptionCall);
  };

  const handleCloseImageOptions = useCallback(() => {
    setShowMoreImageOption(false);
  }, []);

  const handleCloseImageOptionsCall = useCallback(() => {
    setShowMoreImageOptionCall(false);
  }, []);

  const handleSubmitText = async (event: React.FormEvent) => {
    event.preventDefault();
    
    // Check authentication first
    if (!checkAuthAndProceed(() => {})) {
      return;
    }
    
    // Prevent multiple rapid submissions
    if (isSubmitting) {
      return;
    }
    
    // Capture the message content and clear input immediately
    const messageContent = inputValue.trim();
    setInputValue('');
    setIsSubmitting(true);
    
    // Reset hint state when message is sent
    hintState.resetHintState();
    
    try {
      // 1. Check if send happened in voice (call or live) mode
      if (chatStatus === ChatStatus.Call || chatStatus === ChatStatus.LIVE) {
          // In voice mode, send through Gemini Live
          if (!geminiLive.connected) {
            console.warn("Cannot send text: Gemini Live not connected");
            setInputValue(messageContent); // Restore input
            setIsSubmitting(false);
            return;
          }

          // Send text through Gemini Live (this will also add to frontend and DB)
          await geminiLive.sendText(messageContent);

          // Response will be handled by existing assistantTranscription handler
          console.log("✅ Text sent through Gemini Live, expecting voice response");

        } else {
          // 2. Finally, send text as usual (text mode)
          const result = await sendText2API(messageContent);

          if (!result.success) {
            if (result.error === 'insufficient_credits') {
              // Show insufficient credit popup
              setShowInsufficientCreditPopup(true);
              // Restore the input value if credit check fails
              setInputValue(messageContent);
              // Call parent callback if provided
              if (onInsufficientCredit) {
                onInsufficientCredit();
              }
            } else {
              console.error('Failed to send message:', result.error);
            }
          }
        }

    } catch (error) {
      console.error('Error sending message:', error);
    }
    
    // Restore focus to input field after sending
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);

    // Add 0.7 second delay before re-enabling the send button
    setTimeout(() => {
      setIsSubmitting(false);
    }, 700);
  };

  // Function to trigger regen (send empty message)
  const triggerRegen = useCallback(async () => {
    // Prevent multiple rapid submissions
    if (isSubmitting) {
      return;
    }
    
    setIsSubmitting(true);
    
    // Reset hint state when message is sent
    hintState.resetHintState();
    
    try {
      // Use the new API-based send message function with empty content (requests another response)
      const result = await sendText2API('');

      if (!result.success) {
        if (result.error === 'insufficient_credits') {
          // Show insufficient credit popup
          setShowInsufficientCreditPopup(true);
          // Call parent callback if provided
          if (onInsufficientCredit) {
            onInsufficientCredit();
          }
        } else {
          console.error('Failed to send regen message:', result.error);
        }
      }

    } catch (error) {
      console.error('Error sending regen message:', error);
    }

    // Add 0.7 second delay before re-enabling the send button
    setTimeout(() => {
      setIsSubmitting(false);
    }, 700);
  }, [isSubmitting, hintState, sendText2API, onInsufficientCredit]);

  // Expose triggerRegen function to parent component
  useEffect(() => {
    if (onRegenRequest) {
      onRegenRequest(triggerRegen);
    }
  }, [onRegenRequest]); // Remove triggerRegen from dependencies to prevent infinite loop


  const handleSwitchToVideo = async () => {
    // if on live or text chat, leave chat
    if (globalLKRoomState.room && globalLKRoomState.room.state === 'connected') {
      await globalLKRoomState.room.disconnect();
      await new Promise(resolve => setTimeout(resolve, 200));
      setChatStatus(ChatStatus.INFO);
    }

    // Check if high traffic popup skipped
    
    // Set debug text image only flag to false
    globalChatStateActions.setDebugAutoLaunchVideo(true);
  };

  const handleEnterCall = async () => {
    // Check authentication first
    if (!checkAuthAndProceed(() => {})) {
      return;
    }
    
    // Reset resumption handle to start fresh sessions
    geminiLive.resetResumptionHandle();
    
    try {
      console.log("📞 Entering call mode...");
      if (chatStatus === ChatStatus.INFO) {
        setChatStatus(ChatStatus.Call);
      }
      // if in live mode, stay in live mode
      
      // Toggle showing messages when entering call/live mode
      if (onToggleShowMessages){
        if (chatStatus === ChatStatus.LIVE && showMessages) {
          if (isMobile) {
            onToggleShowMessages(); // turn off messages in mobile
          }
        } else if (chatStatus === ChatStatus.Call && !showMessages) {
          onToggleShowMessages(); // turn on messages
        }
      }
      
      // Connect to Gemini Live
      await geminiLive.connect();
      console.log("✅ Connected to Gemini Live voice chat");
    } catch (error) {
      console.error("❌ Failed to connect to Gemini Live:", error);
      // Revert to INFO status if connection fails
      if (globalLKRoomState.room && globalLKRoomState.room.state === 'connected') {
        await globalLKRoomState.room.disconnect();
      }
      if (chatStatus !== ChatStatus.INFO) {
        setChatStatus(ChatStatus.INFO);
      }
    }
  };

  const handleHangupCall = async () => {
    try {
      console.log("📴 Hanging up call...");
      
      // Disconnect from Gemini Live
      await geminiLive.disconnect();
      console.log("✅ Disconnected from Gemini Live voice chat");
      // disconnect from LiveKit room 
      if (globalLKRoomState.room && globalLKRoomState.room.state === 'connected') {
        await globalLKRoomState.room.disconnect();
      }
      
      setChatStatus(ChatStatus.INFO); // change to info status no matter what
      
      // Show messages when exiting call mode
      if (onToggleShowMessages && !showMessages) {
        onToggleShowMessages();
      }
    } catch (error) {
      console.error("❌ Error during hangup:", error);
      // Still change status even if disconnect fails
      if (globalLKRoomState.room && globalLKRoomState.room.state === 'connected') {
        await globalLKRoomState.room.disconnect();
      }
      if (chatStatus !== ChatStatus.INFO) {
        setChatStatus(ChatStatus.INFO);
      }
      
      // Show messages when exiting call mode even if disconnect fails
      if (onToggleShowMessages && !showMessages) {
        onToggleShowMessages();
      }
    }
  };

  const handleToggleMic = () => {
    setMuted(!muted);
  };

  // Auto connect to GeminiLive when entering Live mode
  useEffect(() => {
    if (chatStatus === ChatStatus.LIVE) {
      handleEnterCall();
    }
  }, [chatStatus]);

  // Cleanup: disconnect Gemini Live when leaving call mode
  useEffect(() => {
    return () => {
      if (geminiLive.connected) {
        console.log("🧹 Cleanup: Disconnecting Gemini Live");
        geminiLive.disconnect();
      }
    };
  }, []);

  // Sync geminiLive connected state with chat status
  useEffect(() => {
    // If we're not in voice (call or live) mode but Gemini Live is connected, disconnect it
    if ((chatStatus !== ChatStatus.Call && chatStatus !== ChatStatus.LIVE) && 
        geminiLive.connected) {
      console.log("🔄 Chat status changed from voice (call or live), disconnecting Gemini Live");
      geminiLive.disconnect();
    }
  }, [chatStatus, geminiLive.connected]);

  // Handle low credits in voice mode
  useEffect(() => {
    if (geminiLive.isLowOnCredits && geminiLive.connected) {
      console.log("💰 Low on credits during voice chat, disconnecting and showing popup");
      // Disconnect voice mode
      geminiLive.disconnect();
      // Show insufficient credit popup
      setShowInsufficientCreditPopup(true);
    }
  }, [geminiLive.isLowOnCredits, geminiLive.connected]);

  // Conditional rendering based on chat status
  if (chatStatus === ChatStatus.LOADING) {
    // Show simple loading UI when in LOADING state
    return (
      <div className="flex items-center justify-center w-full h-full px-2 py-2">
        <div className="flex justify-center">
          <button
            disabled
            className="flex items-center justify-center w-64 rounded-full py-2 px-6 transition-colors backdrop-blur-sm bg-gray-600/40 cursor-not-allowed text-white/50"
          >
            <span className="flex items-center gap-2 text-base font-semibold drop-shadow-md animate-pulse">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              {loadingText}
            </span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="px-2 py-2">
        <form onSubmit={handleSubmitText}>
          {/* First row: All control buttons */}
          <div className="flex items-center justify-between mb-2">
            {/* Left side: Image Options Button - Only show when NOT in call mode */}
            {(chatStatus !== ChatStatus.Call && chatStatus !== ChatStatus.LIVE) && (
              <div className="relative">
                <button
                  ref={imageButtonRef}
                  type="button"
                  onClick={handleToggleMoreImageOptions}
                  disabled={isDisabledDueToLoading}
                  className="flex items-center ml-2 justify-center w-auto px-3 h-9 disabled:cursor-not-allowed transition-colors text-xs text-gray-200 hover:text-white rounded-full bg-white/10 backdrop-blur-md hover:bg-white/20 disabled:opacity-50 disabled:hover:bg-white/10 disabled:hover:text-gray-200"
                  title={t('moreImageOptions')}
                >
                  <Image className="w-4 h-4 mr-1" />
                  <span>{t('ask')}</span>
                  {showMoreImageOption ? (
                    <ChevronDown className="w-3 h-3 ml-1" />
                  ) : (
                    <ChevronUp className="w-3 h-3 ml-1" />
                  )}
                </button>

                {/* Image Options Panel - positioned relative to the button */}
                <ImageOptionsPanel
                  imageButtonRef={imageButtonRef}
                  isVisible={showMoreImageOption}
                  onClose={handleCloseImageOptions}
                  onShowMe={handleShowMeClick}
                  onCustomize={handleCustomizeCharacterClick}
                  onShowMeBoobs={handleShowMeBoobsClick}
                  onShowMeNaked={handleShowMeNakedClick}
                />
              </div>
            )}
            {/* Phone Button - Only show when chat status is INFO */}
            {chatStatus === ChatStatus.INFO && (
              <button
                type="button"
                onClick={() => {
                  checkAuthAndProceed(() => {
                    setShowLiveVoicePopup(true);
                    // onStartChat?.();
                  });
                }}
                disabled={isDisabledDueToLoading}
                className={`flex items-center justify-center w-auto px-3 h-8 ml-1 disabled:cursor-not-allowed transition-colors text-xs rounded-full ${
                  character?.metadata?.head_confidence && character?.metadata?.head_confidence >= 0.5
                    ? 'bg-[#5856d6] text-white hover:bg-[#4a46c6] border-2 border-[#5856d6] hover:border-[#4a46c6]'
                    : 'bg-white/10 backdrop-blur-md text-gray-200 hover:text-white hover:bg-white/20 disabled:opacity-50 disabled:hover:bg-white/10 disabled:hover:text-gray-200'
                }`}
                title={t('enterCall')}
              >
                {character?.metadata?.head_confidence && character?.metadata?.head_confidence >= 0.5 ? (
                  <>
                    <Play className="w-4 h-4 mr-1" />
                    <span>{t('liveChat')}</span>
                  </>
                ) : (
                  <>
                    <Phone className="w-4 h-4 mr-1" />
                    <span>{t('call')}</span>
                  </>
                )}
              </button>
            )}

            {/* Center: Mic button (Call mode) or Hangup button (Call mode) */}
            <div className="flex items-center justify-center flex-1">
              {/* Microphone Button - Only show when in voice (call or live) mode */}
              {(chatStatus === ChatStatus.Call || chatStatus === ChatStatus.LIVE) && (
                <button
                  type="button"
                  onClick={handleToggleMic}
                  disabled={isDisabledDueToLoading}
                  className="flex items-center justify-center w-auto px-3 h-8 disabled:cursor-not-allowed transition-colors text-xs rounded-full bg-white/10 backdrop-blur-md text-white hover:bg-white/20 disabled:opacity-50"
                >
                  {(!muted && !onGreetingTurn) ? (
                    <>
                      <Mic className="w-4 h-4 mr-1" />
                      <span>{t('micOn')}</span>
                    </>
                  ) : (
                    <>
                      <MicOff className="w-4 h-4 mr-1" />
                      <span>{t('micOff')}</span>
                    </>
                  )}
                </button>
              )}

              {/* Hangup Button - Only show when in voice (call or live) mode */}
              {(chatStatus === ChatStatus.Call || chatStatus === ChatStatus.LIVE) && (
                <button
                  type="button"
                  onClick={handleHangupCall}
                  className="flex items-center justify-center w-auto px-6 h-8 ml-2 disabled:cursor-not-allowed transition-colors text-xs rounded-full bg-red-600/80 backdrop-blur-md text-white hover:bg-red-700/80 disabled:opacity-50 disabled:hover:bg-red-600/80"
                  title={t('hangupCall')}
                >
                  <PhoneOff className="w-4 h-4 mr-1" />
                  <span>{t('hangUp')}</span>
                </button>
              )}

              {/* Toggle Text Button - Only show when in voice (call or live) mode */}
              {(chatStatus === ChatStatus.Call || chatStatus === ChatStatus.LIVE)
                && onToggleShowMessages && (
                <button
                  type="button"
                  onClick={onToggleShowMessages}
                  className="flex items-center justify-center w-auto px-3 h-8 ml-2 disabled:cursor-not-allowed transition-colors text-xs rounded-full bg-white/10 backdrop-blur-md text-white hover:bg-white/20 disabled:opacity-50"
                  title={showMessages ? t('hideText') : t('showText')}
                >
                  {showMessages ? (
                    <>
                      <EyeOff className="w-4 h-4 mr-1" />
                      <span>{t('hideText')}</span>
                    </>
                  ) : (
                    <>
                      <Eye className="w-4 h-4 mr-1" />
                      <span>{t('showText')}</span>
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Right side: Voice buttons and Phone button */}
            <div className="flex items-center gap-0">
            </div>
          </div>
          
          {/* Call Status Text - Only show when in voice (call or live) mode */}
          {(chatStatus === ChatStatus.Call || chatStatus === ChatStatus.LIVE)
            && (
            <div className="flex justify-center my-0.5 mx-4">
              <span className="text-[11px] text-gray-400">
                {streamingAssistantMessageId !== null || onGreetingTurn 
                ? t('characterTalking') : t('everythingAIGenerated')}
              </span>
            </div>
          )}
          {/* Second row: Message Input with Send Button inside */}
          {((chatStatus !== ChatStatus.Call && chatStatus !== ChatStatus.LIVE) 
            || muted || onGreetingTurn) && (
          <div className="flex items-start gap-2">
            {/* Message Input Container */}
            <div className="flex-1 relative">
              {/* Main container with border */}
              <div className="bg-white/10 backdrop-blur-md border border-white/30 focus-within:border-white/50 rounded-3xl transition-colors">
                {/* Text area with Hint and Send buttons inside */}
                <div className="flex items-center px-2 py-2 gap-2">
                  {/* Additional Image Button - Only for Live/Call status */}
                  {(chatStatus === ChatStatus.LIVE || chatStatus === ChatStatus.Call) && (
                    <div className="relative">
                      <button
                        ref={imageButtonCallRef}
                        type="button"
                        onClick={handleToggleMoreImageOptionsCall}
                        disabled={isDisabledDueToLoading}
                        className="flex items-center justify-center w-8 h-8 disabled:cursor-not-allowed transition-colors rounded-full bg-white/10 backdrop-blur-md hover:bg-white/20 disabled:opacity-50 disabled:hover:bg-white/10 flex-shrink-0"
                        title={t('moreImageOptions')}
                      >
                        <Image className="w-4 h-4" />
                      </button>

                      {/* Image Options Panel - positioned relative to the button */}
                      <ImageOptionsPanel
                        imageButtonRef={imageButtonCallRef}
                        isVisible={showMoreImageOptionCall}
                        onClose={handleCloseImageOptionsCall}
                        onShowMe={handleShowMeClick}
                        onCustomize={handleCustomizeCharacterClick}
                        onShowMeBoobs={handleShowMeBoobsClick}
                        onShowMeNaked={handleShowMeNakedClick}
                      />
                    </div>
                  )}

                  {/* Hint Button - Inside text area at far left */}
                  <button
                    ref={lightbulbButtonRef}
                    type="button"
                    onClick={() => {
                      checkAuthAndProceed(() => {
                        hintState.requestHint();
                      });
                    }}
                    disabled={isDisabledDueToLoading || hintState.isHintLoading}
                    className="flex items-center justify-center w-8 h-8 disabled:cursor-not-allowed transition-colors rounded-full bg-white/10 backdrop-blur-md hover:bg-white/20 disabled:opacity-50 disabled:hover:bg-white/10 flex-shrink-0"
                  >
                    <Lightbulb className={`w-4 h-4 transition-colors ${hintState.showHintPanel ? 'text-yellow-300' : 'text-white'}`} />
                  </button>

                  {/* Text Area */}
                  <textarea
                    value={inputValue}
                    onChange={(e) => {
                      setInputValue(e.target.value);
                      resetInactivityTimer(); // Reset timer when user starts typing
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmitText(e);
                      }
                    }}
                    placeholder=""
                    disabled={isDisabledDueToLoading}
                    className={
                      [
                        "flex-1",
                        "bg-transparent",
                        "border-none",
                        "px-2",
                        "py-1",
                        "text-white",
                        "focus:outline-none",
                        "disabled:opacity-50",
                        "disabled:cursor-not-allowed",
                        "resize-none",
                        "overflow-auto",
                        "min-h-[28px]",
                        "h-[28px]", // Fixed height for one line
                        "leading-5",
                        "[scrollbar-width:none]",
                        "[-ms-overflow-style:none]",
                        "[&::-webkit-scrollbar]:hidden"
                      ].join(" ")
                    }
                    ref={inputRef}
                    rows={1}
                  />

                  {/* Send Button inside input */}
                  <button
                    type="submit"
                    disabled={isDisabledDueToLoading}
                    className="flex items-center justify-center w-8 h-8 disabled:opacity-50 disabled:cursor-not-allowed rounded-full transition-colors flex-shrink-0 backdrop-blur-sm"
                  >
                    <Send className="w-4 h-4 text-white" />
                  </button>
                </div>
              </div>

              {/* Hint Panel */}
              <HintPanel
                lightbulbButtonRef={lightbulbButtonRef}
                isVisible={hintState.showHintPanel}
                isLoading={hintState.isHintLoading}
                hints={hintState.hints}
                onClose={hintState.closeHintPanel}
                onSelectHint={(hintText) => {
                  // Only copy the hint text to input, don't send automatically
                  setInputValue(hintText);
                  // Focus the input after copying
                  if (inputRef.current) {
                    inputRef.current.focus();
                  }
                }}
              />

            </div>
          </div>
          )}

        </form>
      </div>

      {/* Insufficient Credit Popup */}
      <InsufficientCreditPopup
        isOpen={showInsufficientCreditPopup}
        onClose={() => setShowInsufficientCreditPopup(false)}
      />

      {/* Subscription Popup */}
      <SubscriptionPopup 
        isOpen={showSubscriptionPopup} 
        onClose={() => setShowSubscriptionPopup(false)} 
        callbackBaseUrl={currentChatUrl}
      />

      {/* Live Voice Edit Popup for Call */}
      <LiveVoiceEditPopup
        isOpen={showLiveVoicePopup}
        onClose={() => setShowLiveVoicePopup(false)}
        showPricing={false}
        onCallButtonClicked={handleEnterCall}
        onStartChat={onStartChat}
      />

      {/* Image Generation Popup */}
      <ImageGenPopupNew
        isOpen={showImageGenPopup}
        onClose={() => setShowImageGenPopup(false)}
      />

      {/* Login Popup */}
      <LoginPopup 
        isOpen={showLoginPopup} 
        onClose={() => setShowLoginPopup(false)}
        redirectTarget={currentChatUrl}
      />
    </>
  );
}

// PrestartChatControl here
interface PrestartChatControlProps {
  onStartChat?: () => void;
  hasHistory?: boolean;
}

export function PrestartChatControl({ 
  onStartChat, 
  hasHistory
}: PrestartChatControlProps) {
  const t = useTranslations('chat');
  // Simple toggle for high traffic popup - set to false to disable
  const showHighTrafficPopup = false;
  
  const [showInsufficientCreditPopup, setShowInsufficientCreditPopup] = useState(false);
  const [showVoicePopup, setShowVoicePopup] = useState(false);
  const [showHighTrafficPopupState, setShowHighTrafficPopupState] = useState(false);
  const { chatStatus, setChatStatus } = useChatStatus();
  const [globalChatStateData, globalChatStateActions] = useGlobalChatState();

  // Get room from global state
  const { globalLKRoomState } = useGlobalLiveKitRoomState();
  
  // mod email list
  const modEmails = [
    'caozongheng123@gmail.com',
    'mjhjmxmjhjm@gmail.com',
    'jc9562@nyu.edu',
    'yh5022@nyu.edu'
  ]
  // Only check if user is mod after session is loaded
  const { session, userEmail, isAuthenticated, isLoading: authLoading } = useAuth();
  const isMod = !authLoading && session?.user?.email ? modEmails.includes(session.user.email) : false;
  
  // User app data hook for gem count (singleton) - only after auth loading completes
  const [userAppDataState] = useUserAppData(
    !authLoading && isAuthenticated && userEmail ? userEmail : ''
  );
  
  // Timer state for loading animation
  const [loadingTimer, setLoadingTimer] = useState(0);
  const [loadingText, setLoadingText] = useState(t('connecting'));

  const isDisabledDueToLoading = chatStatus === ChatStatus.LOADING;
  
  // Reset timer when chat state changes
  useEffect(() => {
    if (chatStatus === ChatStatus.LOADING) {
      setLoadingTimer(0);
      setLoadingText(t('connecting'));
    }
  }, [chatStatus, t]);
  
  // Timer effect for progressive text changes
  useEffect(() => {
    if (chatStatus === ChatStatus.LOADING) {
      const interval = setInterval(() => {
        setLoadingTimer(prev => {
          const newTime = prev + 1;
          if (newTime === 6) {
            setLoadingText(t('almostThere'));
          } else if (newTime === 30) {
            setLoadingText(t('considerRefreshing'));
          }
          return newTime;
        });
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [chatStatus, t]);
  
  const handleStartChatClick = async (debugVideoOff: boolean = false) => {
    // if on live or text chat, leave chat
    if (chatStatus === ChatStatus.LIVE) {
      if (globalLKRoomState.room && globalLKRoomState.room.state === 'connected') {
        globalLKRoomState.room.disconnect();
        setChatStatus(ChatStatus.INFO);
      }
    }

    // Check if high traffic popup should be shown
    if (showHighTrafficPopup) {
      setShowHighTrafficPopupState(true);
      return;
    }

    // If credits are sufficient or check failed, proceed with starting chat
    onStartChat?.();
  };
  
  const handlePlayBackClick = async () => {
    // Check if high traffic popup should be shown
    if (showHighTrafficPopup) {
      setShowHighTrafficPopupState(true);
      return;
    }
    
    // Set debug text image only flag
    globalChatStateActions.resetFrontendChatHistory();
    await globalChatStateActions.setActiveImage(globalChatStateData.profileImage.imageUri || '');
    globalChatStateActions.setDebugPlayBack(true);
    
    // If credits are sufficient or check failed, proceed with starting chat
    onStartChat?.();
  };

  // Auto-start text chat or video chat for logged-in users
  useEffect(() => {
    const autoStartChat = async () => {
      // Only proceed if user is logged in, not loading, and chat is in INFO state
      if (!authLoading && isAuthenticated) {
        if (globalChatStateData.debugAutoLaunchVideo) {
          // If we need to launch video
          handleStartChatClick(false);
          globalChatStateActions.setDebugAutoLaunchVideo(false);
        }
      }
    };

    // Small delay to ensure component is fully mounted
    const timer = setTimeout(autoStartChat, 50);
    return () => clearTimeout(timer);
  }, [authLoading, isAuthenticated, chatStatus]);

  return (
    <>
      <div className="flex items-center justify-center w-full h-full px-2 py-2">
        <div className="flex gap-3 justify-center">
          {/* Video button - Shows loading spinner and text when LOADING */}
          <button
            onClick={() => handleStartChatClick(false)}
            disabled={isDisabledDueToLoading}
            className={`flex items-center justify-center ${chatStatus === ChatStatus.LOADING ? 'w-64' : 'w-44'} rounded-full py-2 px-6 transition-colors backdrop-blur-sm ${
              isDisabledDueToLoading
                ? 'bg-gray-600/40 cursor-not-allowed text-white/50'
                : 'bg-[#5856d6] hover:bg-[#5856d6] text-white'
            }`}
          >
            {chatStatus === ChatStatus.LOADING ? (
              <span className={`flex items-center gap-2 text-base font-semibold drop-shadow-md ${chatStatus === ChatStatus.LOADING ? 'animate-pulse' : ''}`}>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {loadingText}
              </span>
            ) : (
              <span className="flex items-center gap-2 text-base font-semibold drop-shadow-md">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
                  <path d="M5 5a2 2 0 0 1 3.008-1.728l11.997 6.998a2 2 0 0 1 .003 3.458l-12 7A2 2 0 0 1 5 19z"/>
                </svg>
                {t('liveChat')}
              </span>
            )}
          </button>

          {/* Text button - Hidden when LOADING / The old debug button */}
          {false && (
            <button
              onClick={() => handleStartChatClick(true)}
              disabled={isDisabledDueToLoading}
              className={`flex items-center justify-center w-44 rounded-full py-2 px-6 transition-colors backdrop-blur-sm ${
                isDisabledDueToLoading
                  ? 'bg-gray-600/40 cursor-not-allowed text-white/50'
                  : 'bg-[#5856d6] hover:bg-[#5856d6] text-white'
              }`}
            >
              <span className="flex items-center gap-2 text-base font-semibold drop-shadow-md">
                <MessageSquare className="w-5 h-5" />
                Chat
              </span>
            </button>
          )}
          {/* {isMod && <button 
            onClick={() => handlePlayBackClick()}
            disabled={isDisabled}
            className={`flex items-center justify-center rounded-full py-2 px-4 transition-colors backdrop-blur-sm ${
              isDisabled 
                ? 'bg-gray-600/40 cursor-not-allowed text-white/50'
                : 'bg-[#5856d6] hover:bg-[#5856d6] text-white'
            }`}
          >
            <span className="text-xs font-medium drop-shadow-md">
              [Playback]
            </span>
          </button>} */}
        </div>
      </div>

      <InsufficientCreditPopup
        isOpen={showInsufficientCreditPopup}
        onClose={() => setShowInsufficientCreditPopup(false)}
      />

      {/* High Traffic Popup */}
      <PopupTemplate
        isOpen={showHighTrafficPopupState}
        onClose={() => setShowHighTrafficPopupState(false)}
        closeable={true}
        isTransparent={true}
      >
        <div className="p-8">
          <div className="text-center mb-6">
            <div className="flex justify-center mb-4">
              <Users className="h-12 w-12 text-yellow-500" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-3">Dango is at capacity!</h1>
            <p className="text-gray-300 text-sm leading-relaxed">
              We are currently experiencing a lot of user traffic. Please try again in a few minutes.
            </p>
          </div>
          <div className="space-y-4">
            <button
              onClick={() => setShowHighTrafficPopupState(false)}
              className="w-full flex items-center justify-center gap-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 px-4 rounded-xl transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg"
            >
              <span>Try Again Later</span>
            </button>
          </div>
        </div>
      </PopupTemplate>
    </>
  );
}

